import React, { useState } from 'react'
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Grid,
  GridItem,
  Spinner,
  VStack,
  useToast
} from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

import { useAppDispatch, useAppSelector } from 'src/redux/hooks'
import NotFoundedPatients from 'src/components/not-found/NotFoundPatients'
import FoundedPatients from './FoundedPatients'
import { createPatienteFromTemp, searchPatientsFromTemp } from 'src/view-models/Patient'
import { useNavigate } from 'react-router-dom'

// Zod validation schema
const searchPatientSchema = z.object({
  uuid: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  birthDate: z.string().optional(),
}).refine(
  (data) => data.uuid || data.firstName || data.lastName || data.birthDate,
  {
    message: "Au moins un champ doit être rempli",
    path: ["uuid"],
  }
)

type SearchPatientFormData = z.infer<typeof searchPatientSchema>

interface Props {
  isAddingPatient:boolean,
  setIsAddingPatient:React.Dispatch<React.SetStateAction<boolean>>
  isSearchingPatient:boolean,
  setIsSearchingPatient:React.Dispatch<React.SetStateAction<boolean>>
  onClose: () => void
}

const SearchPatientForm: React.FC<Props> = ({ onClose,isAddingPatient,setIsAddingPatient,isSearchingPatient,setIsSearchingPatient  }) => {
  const dispatch = useAppDispatch()
  const toast = useToast()
  const navigation = useNavigate()
  const [searchResults, setSearchResults] = useState([])
  const [selectedTempPatient, setSelectedTempPatient] = useState<any>(null)


  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch
  } = useForm<SearchPatientFormData>({
    resolver: zodResolver(searchPatientSchema),
    defaultValues: {
      uuid: '',
      firstName: '',
      lastName: '',
      birthDate: ''
    }
  })

  const values = watch()
  const isFormEmpty = !values.uuid && !values.birthDate && !values.firstName && !values.lastName

  const onSubmit = async (data: SearchPatientFormData) => {
    // Build query string from non-empty fields
    setIsSearchingPatient(true)
    const searchedData = Object.entries(data)
      .filter(([key, value]) => value && value.trim() !== '')
      .map(([key, value]) => `${key}=${value}`)
      .join('&')

    const res = await searchPatientsFromTemp("sage-femme", searchedData)
    if (res?.status === true) {
      setSearchResults(res?.data)
    } else {
      setSearchResults([])
    }
    setIsSearchingPatient(false)
  }

  const handleResetForm = () => {
    reset()
    setSearchResults([])
    setSelectedTempPatient(null)
  }

  const handleAddPatient = async () => {
    if (selectedTempPatient) {
      setIsAddingPatient(true)
      const id = selectedTempPatient?.id
      const res = await createPatienteFromTemp(id, toast, dispatch)
      setIsAddingPatient(false)
      if (res?.status === true) {
        navigation(`/sage-femme/patients/${res?.id}/consulter`)
        onClose()

      }
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns="repeat(12, 1fr)" gap={2}>
          <GridItem colSpan={12}>
            <FormControl isInvalid={!!errors.uuid}>
              <FormLabel fontSize="14px" color="#161616">IPP</FormLabel>
              <Input
                {...register('uuid')}
                placeholder="Taper IPP du patient"
                isDisabled={isSearchingPatient || isAddingPatient}
                size="md"
                borderColor="gray.300"
                _hover={{ borderColor: 'gray.400' }}
                _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
              />
              <FormErrorMessage>{errors.uuid?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>

          <GridItem colSpan={{ base: 12, lg: 6 }}>
            <FormControl isInvalid={!!errors.firstName}>
              <FormLabel fontSize="14px" color="#161616">Nom</FormLabel>
              <Input
                {...register('firstName')}
                placeholder="Nom"
                isDisabled={isSearchingPatient || isAddingPatient}
                size="md"
                borderColor="gray.300"
                _hover={{ borderColor: 'gray.400' }}
                _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
              />
              <FormErrorMessage>{errors.firstName?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>

          <GridItem colSpan={{ base: 12, lg: 6 }}>
            <FormControl isInvalid={!!errors.lastName}>
              <FormLabel fontSize="14px" color="#161616">Prénom</FormLabel>
              <Input
                {...register('lastName')}
                placeholder="Prénom"
                isDisabled={isSearchingPatient || isAddingPatient}
                size="md"
                borderColor="gray.300"
                _hover={{ borderColor: 'gray.400' }}
                _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
              />
              <FormErrorMessage>{errors.lastName?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>

          <GridItem colSpan={12}>
            <FormControl mt={2} isInvalid={!!errors.birthDate}>
              <FormLabel fontSize="14px" color="#161616">Date de naissance*</FormLabel>
              <Input
                {...register('birthDate')}
                type="date"
                isDisabled={isSearchingPatient || isAddingPatient}
                size="md"
                borderColor="gray.300"
                _hover={{ borderColor: 'gray.400' }}
                _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
              />
              <FormErrorMessage>{errors.birthDate?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>

        {isSubmitting ? (
          <Box w="100%" display="flex" justifyContent="center" my={3}>
            <Spinner size="lg" color="blue.500" />
          </Box>
        ) : searchResults?.length > 0 ? (
          <FoundedPatients
            searchResults={searchResults}
            selectedTempPatient={selectedTempPatient}
            setSelectedTempPatient={setSelectedTempPatient}
          />
        ) : (
          <NotFoundedPatients />
        )}

        <Grid templateColumns="repeat(12, 1fr)" gap={2} mt={2}>
          <GridItem colSpan={{ base: 12, lg: 6 }}>
            <Button
              variant="outline"
              w="100%"
              isDisabled={isSearchingPatient || isAddingPatient}
              onClick={handleResetForm}
              colorScheme="gray"
            >
              Réinitialiser
            </Button>
          </GridItem>

          <GridItem colSpan={{ base: 12, lg: 6 }}>
            {!selectedTempPatient ? (
              <Button
                type="submit"
                w="100%"
                isDisabled={isSearchingPatient || isAddingPatient || isFormEmpty}
                colorScheme="blue"
                bg="#215EE0"
                _hover={{ bg: '#1a4db3' }}
              >
                {isSearchingPatient ? "Recherche en cours..." : "Recherche"}
              </Button>
            ) : (
              <Button
                type="button"
                w="100%"
                isDisabled={isSearchingPatient || isAddingPatient}
                onClick={handleAddPatient}
                colorScheme="blue"
                bg="#215EE0"
                _hover={{ bg: '#1a4db3' }}
              >
                {isAddingPatient ? "Ajout en cours..." : "Ajouter le patient"}
              </Button>
            )}
          </GridItem>
        </Grid>
      </VStack>
    </form>
  )
}

export default SearchPatientForm